import { importMultimediaFromUrlRequest } from "@services/multimedia/multimediaApi";
import { inferMediaType, resolveMediaUrl } from "@utils/media/mediaUrl";

function escapeHtml(value = "") {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function formatBeforeYouGoImportedRichText(value = "") {
  const normalizedText = String(value || "").replace(/\r\n?/g, "\n").trim();
  if (!normalizedText) return "";

  const paragraphs = normalizedText.split(/\n\n/).map((paragraph) => {
    const lines = paragraph.split("\n").map((line) => {
      const escapedLine = escapeHtml(line);
      return escapedLine.replace(
        /(^|[\s>])#([^\s#<]+)/g,
        (_, prefix, content) => `${prefix}<strong>${content}</strong>`,
      );
    });
    return `<p>${lines.join("<br />") || "<br />"}</p>`;
  });

  return paragraphs.join("");
}

function createLocalizedValues(source = {}, activeIsos = [], formatter = null) {
  const values = source && typeof source === "object" ? source : {};
  return activeIsos.reduce((acc, iso) => {
    const rawValue = values?.[iso] ?? "";
    acc[iso] = formatter ? formatter(rawValue) : String(rawValue || "");
    return acc;
  }, {});
}

function createEmptyTranslationIds(activeIsos = []) {
  return activeIsos.reduce((acc, iso) => {
    acc[iso] = "";
    return acc;
  }, {});
}

function resolveIconName(iconUrl = "") {
  const value = String(iconUrl || "").toLowerCase();
  if (value.includes("compass")) return "compass";
  if (value.includes("route")) return "route";
  if (value.includes("bed")) return "bed";
  if (value.includes("car")) return "car";
  if (value.includes("restaurant") || value.includes("food")) return "restaurant";
  if (
    value.includes("festival") ||
    value.includes("activit") ||
    value.includes("confetti")
  ) {
    return "festival_activities";
  }
  return "star";
}

function readImportedMultimedia(response = {}) {
  const body = response?.body || {};
  const rawItem = Array.isArray(body?.multimediaList)
    ? body.multimediaList[0]
    : Array.isArray(body?.multimedia)
      ? body.multimedia[0]
      : body?.multimedia || null;
  if (!rawItem) return null;
  const rawUrl = rawItem?.url || rawItem?.mmd_url || "";
  return {
    id: rawItem?.id || rawItem?.mmd_id || "",
    url: resolveMediaUrl(rawUrl),
    mediaType:
      rawItem?.mediaType || rawItem?.mmd_media_type || inferMediaType(rawUrl),
    mimeType: rawItem?.mimeType || rawItem?.mmd_mime_type || "image/*",
  };
}

async function importImageFromUrl(url = "", cache) {
  const normalizedUrl = String(url || "").trim();
  if (!normalizedUrl) return null;
  if (cache.has(normalizedUrl)) return cache.get(normalizedUrl);

  const importPromise = /firebasestorage\.googleapis\.com/i.test(normalizedUrl)
    ? importMultimediaFromUrlRequest(normalizedUrl).then((response) => {
        const media = readImportedMultimedia(response);
        if (!media?.url) throw new Error("The backend did not return imported multimedia.");
        return media;
      })
    : Promise.resolve({
        id: "",
        url: resolveMediaUrl(normalizedUrl),
        mediaType: "image",
        mimeType: "image/*",
      });

  cache.set(normalizedUrl, importPromise);
  return importPromise;
}

function sortByOrder(items = []) {
  return [...items].sort(
    (first, second) => Number(first?.order || 0) - Number(second?.order || 0),
  );
}

async function importTourSectionJsonFile(
  file,
  activeIsos = [],
  onProgress = null,
  {
    rootKey,
    itemDescriptionKey,
    localIdPrefix,
    sectionLabel,
  },
) {
  if (!file) throw new Error("Select a JSON file.");
  if (!/\.json$/i.test(String(file?.name || ""))) {
    throw new Error("Only JSON files are allowed.");
  }

  let parsed;
  try {
    parsed = JSON.parse(await file.text());
  } catch (error) {
    throw new Error("The selected file is not valid JSON.");
  }

  const rawSections = Array.isArray(parsed?.[rootKey]) ? parsed[rootKey] : [];
  if (!rawSections.length) {
    throw new Error(`The JSON must contain a non-empty "${rootKey}" array.`);
  }
  if (!activeIsos.length) {
    throw new Error("The tour does not have configured languages.");
  }

  const imageCache = new Map();
  const orderedSections = sortByOrder(rawSections);
  const importedSections = [];

  for (let sectionIndex = 0; sectionIndex < orderedSections.length; sectionIndex += 1) {
    const rawSection = orderedSections[sectionIndex] || {};
    onProgress?.(`Importing section ${sectionIndex + 1} of ${orderedSections.length}...`);
    let sectionImage = null;
    try {
      sectionImage = await importImageFromUrl(rawSection.imageUrl, imageCache);
    } catch (error) {
      throw new Error(
        `Could not import the image for section ${sectionIndex + 1}: ${error.message}`,
      );
    }

    const sectionLocalId = `${localIdPrefix}-${Date.now()}-${sectionIndex + 1}`;
    const orderedItems = sortByOrder(
      Array.isArray(rawSection?.items) ? rawSection.items : [],
    );
    const importedItems = [];

    for (let itemIndex = 0; itemIndex < orderedItems.length; itemIndex += 1) {
      const rawItem = orderedItems[itemIndex] || {};
      onProgress?.(
        `Importing section ${sectionIndex + 1}, item ${itemIndex + 1} of ${orderedItems.length}...`,
      );
      let itemImage = null;
      try {
        itemImage = await importImageFromUrl(rawItem.imageUrl, imageCache);
      } catch (error) {
        throw new Error(
          `Could not import the image for item ${itemIndex + 1} in section ${sectionIndex + 1}: ${error.message}`,
        );
      }
      importedItems.push({
        id: `${sectionLocalId}-item-${itemIndex + 1}`,
        itemType: String(rawItem?.itemType || "hotel"),
        order: itemIndex + 1,
        displayOrder: itemIndex + 1,
        title: createLocalizedValues(rawItem?.title, activeIsos),
        titleTranslationIds: createEmptyTranslationIds(activeIsos),
        location: String(rawItem?.location || ""),
        description: createLocalizedValues(
          rawItem?.[itemDescriptionKey],
          activeIsos,
          formatBeforeYouGoImportedRichText,
        ),
        descriptionTranslationIds: createEmptyTranslationIds(activeIsos),
        rating:
          rawItem?.rating === null || rawItem?.rating === undefined
            ? ""
            : String(rawItem.rating),
        websiteUrl: String(rawItem?.websiteUrl || ""),
        imageUrl: itemImage?.url || "",
        image: itemImage,
      });
    }

    importedSections.push({
      id: sectionLocalId,
      itemType: String(rawSection?.itemType || "category") || "category",
      order: sectionIndex + 1,
      title: createLocalizedValues(rawSection?.title, activeIsos),
      titleTranslationIds: createEmptyTranslationIds(activeIsos),
      iconName: resolveIconName(rawSection?.iconUrl),
      imageUrl: sectionImage?.url || "",
      image: sectionImage,
      descriptions: createLocalizedValues(
        rawSection?.description,
        activeIsos,
        formatBeforeYouGoImportedRichText,
      ),
      descriptionTranslationIds: createEmptyTranslationIds(activeIsos),
      items: importedItems,
    });
  }

  onProgress?.(`${sectionLabel} import completed.`);
  return importedSections;
}

export function importBeforeYouGoJsonFile(
  file,
  activeIsos = [],
  onProgress = null,
) {
  return importTourSectionJsonFile(file, activeIsos, onProgress, {
    rootKey: "beforeYouGo",
    itemDescriptionKey: "description",
    localIdPrefix: "before-you-go-import",
    sectionLabel: "Before You Go",
  });
}

export function importMaximizeExperienceJsonFile(
  file,
  activeIsos = [],
  onProgress = null,
) {
  return importTourSectionJsonFile(file, activeIsos, onProgress, {
    rootKey: "MaximizeExperience",
    itemDescriptionKey: "descriptions",
    localIdPrefix: "maximize-experience-import",
    sectionLabel: "Maximize Your Experience",
  });
}
